import { CommonModule } from '@angular/common';
import { Component, HostBinding, computed, effect, inject, input } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { MOBILE_GRID_LAYOUT } from 'src/app/category/enum/category-page.enum';
import { CashPointSvgComponent } from 'src/app/components/cash-point-svg/cash-point-svg.component';
import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import { CategoryLayoutData } from 'src/app/core/interfaces/store-configuration.interface';
import { BrowserApiService } from 'src/app/core/services/browser-api/browser-api.service';
import { DeviceDetectionService } from 'src/app/core/services/device-detection/device-detection.service';
import { OverlayService } from 'src/app/core/services/overlay/overlay.service';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import { createDefaultCategoryLayoutData } from 'src/app/core/utilities/type-defaults.util';
import { DealCountdownService } from 'src/app/products/services/deal-countdown.service';
import { PriceService } from 'src/app/products/services/price.service';
import { CashpointsPipe } from 'src/app/shared/pipes/cashpoints.pipe';
import { IndianCurrencyPipe } from 'src/app/shared/pipes/indian-currency.pipe';
import { MathFloorPipe } from 'src/app/shared/pipes/math-floor.pipe';
import { ProductListingPercentageSavePipe } from 'src/app/shared/pipes/product-listing-percentage-save.pipe';
import { AddToCartActionComponent } from "../add-to-cart-action/add-to-cart-action.component";
import { ProductVariantManagementComponent } from '../product-varient-management/product-varient-management.component';

@Component({
  standalone: true,
  selector: 'product-card-square',
  templateUrl: './product-card-square.component.html',
  styleUrls: ['./product-card-square.component.scss'],
  providers: [DealCountdownService],
  imports: [
    CommonModule,
    RouterModule,
    AddToCartActionComponent,
    CashPointSvgComponent,
    CashpointsPipe,
    IndianCurrencyPipe,
    MathFloorPipe,
    ProductListingPercentageSavePipe
  ]
})

export class ProductCardSquareComponent extends ProductVariantManagementComponent {
  protected override router: Router;
  protected override priceService: PriceService;
  protected override overlayService: OverlayService;
  protected override browserApiService: BrowserApiService;
  protected override dealCountdownService: DealCountdownService = inject(DealCountdownService);
  private readonly deviceDetectionService = inject(DeviceDetectionService);
  private readonly storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);

  @HostBinding('style.width') width: string = '';
  IMAGE_URLS = IMAGE_URLS;



  readonly selectedGridViewLayout = input<string>("");
  readonly categoryLayoutData = input<CategoryLayoutData>(createDefaultCategoryLayoutData());

  // Expose device detection service signals for template usage
  get isMobile() {
    return this.deviceDetectionService.isMobile;
  }

  readonly selectedMobileGridLayout = input<MOBILE_GRID_LAYOUT>(MOBILE_GRID_LAYOUT.GRID_LAYOUT1);

  // Expose MOBILE_GRID_LAYOUT enum for template usage
  readonly MOBILE_GRID_LAYOUT = MOBILE_GRID_LAYOUT;

  // Getter to expose product from parent component
  override get product() {
    return super.product;
  }

  // Store configuration signal for cashpoints feature
  readonly storeConfiguration = computed(() => this.storeConfigurationRepositoryService.storeConfigurations());

  // Computed property for cash points
  readonly cashPoints = computed(() => {
    const priceList = this.productVariant?.price_list;
    if (!priceList) return 0;

    const cashpointsPipe = new CashpointsPipe();
    return cashpointsPipe.transform(priceList);
  });

  constructor() {
    // Get dependencies before calling super to avoid circular dependency
    const router = inject(Router);
    const priceService = inject(PriceService);
    const overlayService = inject(OverlayService);
    const browserApiService = inject(BrowserApiService);
    const dealCountdownService = inject(DealCountdownService);

    super(); // Call parent constructor

    // Parent class dependencies are now injected via inject() function
    this.router = router;
    this.priceService = priceService;
    this.overlayService = overlayService;
    this.browserApiService = browserApiService;
    this.dealCountdownService = dealCountdownService;

    effect(() => {
      // This effect will run whenever device type, grid layout, or mobile grid layout changes
      const deviceType = this.deviceDetectionService.deviceType();
      const gridLayout = this.selectedGridViewLayout(); // Use signal input instead of private property
      const mobileGridLayout = this.selectedMobileGridLayout(); // Use signal input instead of private property

      if (deviceType && gridLayout) {
        this.applyStyleBasedOnDevice(gridLayout);
      }
    });
  }

  applyStyleBasedOnDevice(value: string) {
    // Use DeviceDetectionService signals instead of the old isMobile property
    const isMobileDevice = this.deviceDetectionService.isMobile();

    if (!isMobileDevice) {
      switch (value) {
        case "gridViewLayout1":
          this.width = 'calc((100% - 20px) / 2)';
          break;
        case "gridViewLayout2":
          this.width = 'calc((100% - 40px) / 3)';
          break;
        case "gridViewLayout4":
          this.width = 'calc((100% - 60px) / 4)';
          break;
        default:
          this.width = 'calc((100% - 40px) / 3)';
          break;
      }
    } else {
      if (this.selectedMobileGridLayout() === MOBILE_GRID_LAYOUT.GRID_LAYOUT2) {
        this.width = 'calc((50% - 5px))';
      } else {
        this.width = 'calc((100% - 0px))';
      }
    }
  }


  get isAddToCartVisible() {
    // Use DeviceDetectionService signals instead of the old isMobile property
    const isMobileDevice = this.deviceDetectionService.isMobile();
    const categoryLayoutData = this.categoryLayoutData();

    const desktopVisible = categoryLayoutData?.productCardData?.advanceConfiguration?.desktop?.isAddToCartVisible;
    const mobileVisible = categoryLayoutData?.productCardData?.advanceConfiguration?.mobile?.isAddToCartVisible;

    return (!isMobileDevice && desktopVisible) || (isMobileDevice && mobileVisible);
  }

  get isPreviewDetailsVisible() {
    // Use DeviceDetectionService signals instead of the old isMobile property
    const isMobileDevice = this.deviceDetectionService.isMobile();
    const categoryLayoutData = this.categoryLayoutData();

    const desktopVisible = categoryLayoutData?.productCardData?.advanceConfiguration?.desktop?.isPreviewDetailsVisible;
    const mobileVisible = categoryLayoutData?.productCardData?.advanceConfiguration?.mobile?.isPreviewDetailsVisible;

    return (!isMobileDevice && desktopVisible) || (isMobileDevice && mobileVisible);
  }

  openProductPage(urlPath: string) {
    this.browserApiService.open('/products/' + urlPath, '_blank', 'noopener');
  }
}

